import { useState, useEffect, useCallback } from "react";

const isValidJSONArray = (str: string | null) => {
  if (!str) return false;

  try {
    if (Array.isArray(JSON.parse(str))) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

const fetchJSONFromLocalStorage = () => {
  const rawValue = localStorage.getItem("recentSearches");

  if (!rawValue) return [];

  return JSON.parse(rawValue) as string[];
};

const onlyUnique = (value: string, index: number, self: string[]) => self.indexOf(value) === index;

const setInLocalStorage = (newValue: string[]) => {
  localStorage.setItem("recentSearches", JSON.stringify(newValue));

  const event = new CustomEvent("recentSearchInserted", { detail: newValue });
  window.dispatchEvent(event);
};

const useRecentSearches = () => {
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  const handleStorageEvent = useCallback(() => {
    setRecentSearches(fetchJSONFromLocalStorage());
  }, []);

  const setter = useCallback((searchInput: string) => {
    const oldValue = fetchJSONFromLocalStorage();
    const newValue = [searchInput, ...oldValue].filter(onlyUnique).slice(0, 5);

    setInLocalStorage(newValue);
  }, []);

  useEffect(() => {
    // Make sure we initialize the localStorage with a valid JSON string
    if (!isValidJSONArray(localStorage.getItem("recentSearches"))) {
      localStorage.setItem("recentSearches", "[]");
    }

    setRecentSearches(fetchJSONFromLocalStorage());

    window.addEventListener("recentSearchInserted", handleStorageEvent);

    return () => {
      window.removeEventListener("recentSearchInserted", handleStorageEvent);
    };
  }, [handleStorageEvent]);

  return [
    recentSearches,
    setter,
    () => {
      setInLocalStorage([]);
    },
  ] as const;
};

export default useRecentSearches;
